import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  createReservation,
  getReservations,
  getReservationById,
  updateReservation,
  deleteReservation,
  assignTableToReservation,
  acceptReservation,
  rejectReservation,
  markDepositPaid,
  sendDepositLink,
  cancelDepositLink,
  rejectAndRefundDeposit,
  terminalPaymentReservation
} from '@/api/new-api/reservations/reservation'

// Async thunks

export const fetchReservations = createAsyncThunk(
  'reservation/fetchReservations',
  async (
    {
      restaurant_id,
      page = 1,
      limit = 10,
      status,
      reservation_type,
      start_date,
      end_date,
      start_time,
      search
    },
    { rejectWithValue }
  ) => {
    const res = await getReservations(
      restaurant_id,
      page,
      limit,
      start_date,
      end_date,
      status,
      reservation_type,
      start_time,
      search
    )
    if (res.status === 'success') return res?.data || {}
    return rejectWithValue(res.message)
  }
)

export const fetchReservationById = createAsyncThunk(
  'reservation/fetchReservationById',
  async (reservation_id, { rejectWithValue }) => {
    const res = await getReservationById(reservation_id)
    if (res.status === 'success') return res.data?.reservation
    return rejectWithValue(res.message)
  }
)

export const addReservation = createAsyncThunk(
  'reservation/addReservation',
  async (data, { rejectWithValue }) => {
    const res = await createReservation(data)
    if (res.status === 'success') return res?.data?.reservation
    return rejectWithValue(res.message)
  }
)

export const editReservation = createAsyncThunk(
  'reservation/editReservation',
  async ({ reservation_id, data }, { rejectWithValue }) => {
    const res = await updateReservation(reservation_id, data)
    if (res.status === 'success') return res.data
    return rejectWithValue(res.message)
  }
)

export const removeReservation = createAsyncThunk(
  'reservation/removeReservation',
  async (reservation_id, { rejectWithValue }) => {
    const res = await deleteReservation(reservation_id)
    if (res.status === 'success') return { id: reservation_id }
    return rejectWithValue(res.message)
  }
)

export const assignTable = createAsyncThunk(
  'reservation/assignTable',
  async ({ reservation_id, data }, { rejectWithValue }) => {
    const res = await assignTableToReservation(reservation_id, data)
    if (res.status === 'success') return res.data
    return rejectWithValue(res.message)
  }
)

export const acceptReservationThunk = createAsyncThunk(
  'reservation/acceptReservation',
  async (reservation_id, { rejectWithValue }) => {
    const res = await acceptReservation(reservation_id)
    if (res.status === 'success') return res.data.reservations
    return rejectWithValue(res.message)
  }
)

export const rejectReservationThunk = createAsyncThunk(
  'reservation/rejectReservation',
  async ({ reservation_id, data }, { rejectWithValue }) => {
    const res = await rejectReservation(reservation_id, data)
    if (res.status === 'success') return res.data
    return rejectWithValue(res.message)
  }
)

export const markDepositPaidThunk = createAsyncThunk(
  'reservation/markDepositPaid',
  async (reservation_id, { rejectWithValue }) => {
    const res = await markDepositPaid(reservation_id)
    if (res.status === 'success') return res.data
    return rejectWithValue(res.message)
  }
)

export const sendDepositLinkThunk = createAsyncThunk(
  'reservation/sendDepositLink',
  async ({ reservation_id, channel }, { rejectWithValue }) => {
    const res = await sendDepositLink(reservation_id, channel)
    if (res.status === 'success') return res
    return rejectWithValue(res)
  }
)

export const cancelDepositLinkThunk = createAsyncThunk(
  'reservation/cancelDepositLink',
  async (reservation_id, { rejectWithValue }) => {
    const res = await cancelDepositLink(reservation_id)
    if (res.status === 'success') return res
    return rejectWithValue(res.message || 'Failed to cancel deposit payment link')
  }
)

export const rejectAndRefundDepositThunk = createAsyncThunk(
  'reservation/rejectAndRefundDeposit',
  async (reservation_id, { rejectWithValue }) => {
    const res = await rejectAndRefundDeposit(reservation_id)
    if (res.status === 'success') return res
    return rejectWithValue(res.message)
  }
)

export const terminalPaymentReservationThunk = createAsyncThunk(
  'reservation/terminalPaymentReservation',
  async (reservation_id, { rejectWithValue }) => {
    const res = await terminalPaymentReservation(reservation_id)
    if (res.status === 'success') return res
    return rejectWithValue(res.message || 'Payment request failed')
  }
)


//  Initial State
const initialState = {
  reservations: [],
  summary: {},
  singleReservation: null,
  pagination: null,

  loading: false,
  singleLoading: false,
  addLoading: false,
  updateLoading: false,
  deleteLoading: false,
  assignLoading: false,

  success: null,
  error: null
}

// 🔹 Slice
const reservationSlice = createSlice({
  name: 'reservation',
  initialState,
  reducers: {
    clearReservationMessages: state => {
      state.success = null
      state.error = null
    },
    addNewReservation: (state, action) => {
     // add new reservation if not exists at the beginning of the list
      const exists = state.reservations.some(r => r.id === action.payload.id)
      if (!exists) state.reservations.unshift(action.payload)

    },
    updateReservationState : (state, action) => {
      if (state.singleReservation && state.singleReservation.id === action.payload.id) {
        state.singleReservation = action.payload
      }
      const index = state.reservations.findIndex(r => r.id === action.payload.id)
      if (index !== -1) state.reservations[index] = action.payload
    }
  },
  extraReducers: builder => {
    builder
      //  Fetch all reservations
      .addCase(fetchReservations.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReservations.fulfilled, (state, action) => {
        state.loading = false
        state.reservations = action.payload.reservations || []
        state.summary = action.payload.stats || {}
        state.pagination = action.payload.pagination || null
        state.success = 'Reservations fetched successfully!'
      })
      .addCase(fetchReservations.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.reservations = []
        state.summary = {}
        state.pagination = null
      })

      //  Fetch single reservation
      .addCase(fetchReservationById.pending, state => {
        state.singleLoading = true
        state.error = null
      })
      .addCase(fetchReservationById.fulfilled, (state, action) => {
        state.singleLoading = false
        state.singleReservation = action.payload
        state.success = 'Reservation fetched successfully!'
      })
      .addCase(fetchReservationById.rejected, (state, action) => {
        state.singleLoading = false
        state.error = action.payload
        state.singleReservation = null
      })

      //  Create reservation
      .addCase(addReservation.pending, state => {
        state.addLoading = true
        state.error = null
      })
      .addCase(addReservation.fulfilled, (state, action) => {
        state.addLoading = false
        state.reservations.unshift(action.payload)
        state.success = 'Reservation created successfully!'
      })
      .addCase(addReservation.rejected, (state, action) => {
        state.addLoading = false
        state.error = action.payload
      })

      //  Update reservation
      .addCase(editReservation.pending, state => {
        state.updateLoading = true
        state.error = null
      })
      .addCase(editReservation.fulfilled, (state, action) => {
        state.updateLoading = false
        const index = state.reservations.findIndex(
          r => r.id === action.payload.id
        )
        if (index !== -1) state.reservations[index] = action.payload
        state.success = 'Reservation updated successfully!'
      })
      .addCase(editReservation.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
      })

      //  Delete reservation
      .addCase(removeReservation.pending, state => {
        state.deleteLoading = true
        state.error = null
      })
      .addCase(removeReservation.fulfilled, (state, action) => {
        state.deleteLoading = false
        state.reservations = state.reservations.filter(
          r => r.id !== action.payload.id
        )
        state.success = 'Reservation deleted successfully!'
      })
      .addCase(removeReservation.rejected, (state, action) => {
        state.deleteLoading = false
        state.error = action.payload
      })

      //  Assign table
      .addCase(assignTable.pending, state => {
        state.assignLoading = true
        state.error = null
      })
      .addCase(assignTable.fulfilled, (state, action) => {
        state.assignLoading = false
        const idx = state.reservations.findIndex(
          r => r.id === action.payload.id
        )
        if (idx !== -1) state.reservations[idx] = action.payload
        state.success = 'Table assigned successfully!'
      })
      .addCase(assignTable.rejected, (state, action) => {
        state.assignLoading = false
        state.error = action.payload
      })
      .addCase(acceptReservationThunk.pending, state => {
        state.assignLoading = true
        state.error = null
      })
      .addCase(acceptReservationThunk.fulfilled, (state, action) => {
        state.assignLoading = false
        const idx = state.reservations.findIndex(
          r => r.id === action.payload.id
        )
        if (idx !== -1) state.reservations[idx] = action.payload
        state.success = 'Reservation accepted successfully!'
      })
      .addCase(acceptReservationThunk.rejected, (state, action) => {
        state.assignLoading = false
        state.error = action.payload
      })
      .addCase(rejectReservationThunk.pending, state => {
        state.assignLoading = true // Reusing assignLoading or could add rejectLoading
        state.error = null
      })
      .addCase(rejectReservationThunk.fulfilled, (state, action) => {
        state.assignLoading = false
        const idx = state.reservations.findIndex(
          r => r.id === action.payload.id
        )
        if (idx !== -1) state.reservations[idx] = action.payload
        state.success = 'Reservation rejected successfully!'
      })
      .addCase(rejectReservationThunk.rejected, (state, action) => {
        state.assignLoading = false
        state.error = action.payload
      })

      //  Mark deposit paid
      .addCase(markDepositPaidThunk.pending, state => {
        state.updateLoading = true
        state.error = null
      })
      .addCase(markDepositPaidThunk.fulfilled, (state, action) => {
        state.updateLoading = false
        if (state.singleReservation) {
          state.singleReservation.is_deposit_paid = action.payload?.is_deposit_paid ?? true
        }
        const idx = state.reservations.findIndex(
          r => r.id === action.payload?.id
        )
        if (idx !== -1) {
          state.reservations[idx].is_deposit_paid = action.payload?.is_deposit_paid ?? true
        }
        state.success = action.payload?.message || 'Deposit marked as paid successfully!'
      })
      .addCase(markDepositPaidThunk.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
      })

      //  Send deposit link
      .addCase(sendDepositLinkThunk.pending, state => {
        state.updateLoading = true
        state.error = null
      })
      .addCase(sendDepositLinkThunk.fulfilled, (state, action) => {
        state.updateLoading = false
        state.success = action.payload?.message || 'Deposit payment link sent successfully.'
      })
      .addCase(sendDepositLinkThunk.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
      })

      //  Reject and refund deposit (cancel reservation)
      .addCase(rejectAndRefundDepositThunk.pending, state => {
        state.updateLoading = true
        state.error = null
      })
      .addCase(rejectAndRefundDepositThunk.fulfilled, (state, action) => {
        state.updateLoading = false
        const payloadData = action.payload?.data || action.payload
        const reservationData = payloadData?.reservation || payloadData
        if (state.singleReservation) {
          state.singleReservation.deposit_payment_info = reservationData?.deposit_payment_info ?? state.singleReservation.deposit_payment_info
          state.singleReservation.status = reservationData?.status || "cancelled"
        }
        const resId = reservationData?.id || payloadData?.id
        const idx = state.reservations.findIndex(
          r => r.id === resId
        )
        if (idx !== -1) {
          state.reservations[idx].deposit_payment_info = reservationData?.deposit_payment_info ?? state.reservations[idx].deposit_payment_info
          state.reservations[idx].status = reservationData?.status || "cancelled"
        }
        state.success = action.payload?.message || 'Reservation cancelled successfully.'
      })
      .addCase(rejectAndRefundDepositThunk.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
      })

      //  Terminal payment (card payment)
      .addCase(terminalPaymentReservationThunk.pending, state => {
        state.updateLoading = true
        state.error = null
      })
      .addCase(terminalPaymentReservationThunk.fulfilled, (state, action) => {
        state.updateLoading = false
        state.success = action.payload?.message || 'Payment dispatched to terminal successfully!'
      })
      .addCase(terminalPaymentReservationThunk.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
      })
  }
})

export const { clearReservationMessages, addNewReservation, updateReservationState } = reservationSlice.actions
export default reservationSlice.reducer
